import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Pagination from '@mui/material/Pagination';
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import {Filters, Preloader} from "@vedmarket/ui-kit";

import {
  getHscodeDataMutation,
  useLazyGetCertificationCompaniesForFilterQuery,
  useParamsHsCode,
} from "../../../../../../../../../api/calc";

import {useGetCalcIdFromRoute} from "../../../../../../../../../utils/hooks";

import CompanyCard from "../company-card";
import {ADORNMENTS} from "../../../../../../../../../config/constants";
import './modalSearch.scss';

export const enum SORTING_OPTIONS {
    COST_DESK = 'cost_total',
    COST_ASC = '-cost_total',
}

export const enum RESULTS_COUNT {
    COUNT_10 = 10,
    COUNT_30 = 30,
    COUNT_45 = 45
}

type Props = {
    preloadCenters?: {
        results: HsCodeOutputItem[]
        count: number
        meta: HsCodeOutputMeta
    }
    code: string
    countryCode: string
    techReglament: string
    handleClose(): void
}

type FilterValues = {
  cost: [number, number]
  companyIds: number[]
}

const getCompanyFilterData = ({ company_id, ...filterData }: HsCodeInput) => filterData;

const ModalSearch = ({preloadCenters, handleClose, code, countryCode, techReglament} : Props ) => {

  const { t } = useTranslation()

  const [ centers, setCenters ] = React.useState<HsCodeOutputItem[]>(preloadCenters?.results || []);
  const [ totalCenters, setTotalCenters ] = React.useState(preloadCenters?.results.length);
  const [ minTotal, setMinTotal ] = React.useState(preloadCenters?.meta?.min_cost || 0);
  const [ maxTotal, setMaxTotal ] = React.useState(preloadCenters?.meta?.max_cost || 100000);
  const [ filtersValues, setFiltersValues ] = React.useState<FilterValues>({
    cost: [ preloadCenters?.meta?.min_cost || 0, preloadCenters?.meta?.max_cost || 100000 ],
    companyIds: [],
  });

  const [ showProgressBar, setShowProgressBar ] = useState(true);
  const [ filtersAreApplying, setFiltersAreApplying ] = useState(false);

  const orderId = useGetCalcIdFromRoute()
  const filterData = useParamsHsCode();
  const [ companiesFilterData ] = useState(() => getCompanyFilterData(filterData));
  const [ getCompaniesForFilter, { data: companiesList } ] = useLazyGetCertificationCompaniesForFilterQuery()

  const [ getHscodeData ] = getHscodeDataMutation()

  useEffect(() => {
    if (companiesFilterData.code_params) {
      getCompaniesForFilter({
        ...companiesFilterData,
        search: 'центр',
      });
    }
  }, [ companiesFilterData, getCompaniesForFilter ])

  useEffect(() => {
    if (preloadCenters?.results){
      setCenters(preloadCenters?.results);
      setTotalCenters(preloadCenters?.count || preloadCenters?.results.length);
    }
    if (preloadCenters?.meta) {
      const nextMinTotal = preloadCenters.meta.min_cost || 0;
      const nextMaxTotal = preloadCenters.meta.max_cost || 100000;

      setMinTotal(nextMinTotal);
      setMaxTotal(nextMaxTotal);
      setFiltersValues((state) => ({
        ...state,
        cost: [ nextMinTotal, nextMaxTotal ],
      }));
    }
  }, [ preloadCenters ])

  useEffect(() => {
    if (companiesList) {
      const companyIds = companiesList.results.map((item) => item.id);

      setFiltersValues((state) => ({
        ...state,
        companyIds,
      }));
    }
  }, [ companiesList ])

  useEffect(() => {
    setTimeout(() => {
      setShowProgressBar(false)
    }, 1000);
  }, [ ])

  const centersRequest = (data?: OrdersParamsCertCenters) => {
    return getHscodeData({
      ...filterData,
      orderId,
      ...data,
    }).then((data) => {
      // @ts-ignore
      setCenters(data.data.results);
      // @ts-ignore
      setTotalCenters(data.data.count);
    });
  }

  const handleChangeResultsCount = (value: string | RESULTS_COUNT ) => centersRequest({
    limit: Number(value)
  });

  const handleChangeCurrentPage = (event: React.ChangeEvent<unknown>, pageNumber: number) => centersRequest({
    page: pageNumber
  });

  const resultsCount = filterData.limit || RESULTS_COUNT.COUNT_30;
  const companyIds = companiesList?.results.map((item) => item.id) || [];
  const companyOptions = companiesList?.results.map((item) => ({
    value: item.id,
    label: item.name,
  })) || [];

  const handleChangeFilters = (values: FilterValues) => {
    setFiltersValues(values);
  }

  const handleApplyFilters = (values: FilterValues) => {
    const isDefaultCost = values.cost[0] === minTotal && values.cost[1] === maxTotal;
    const hasDefaultCompanyIds = values.companyIds.length === companyIds.length
      && companyIds.every((item) => values.companyIds.includes(item));

    setFiltersValues(values);
    setFiltersAreApplying(true);

    centersRequest({
      cost_total_min: isDefaultCost ? undefined : values.cost[0],
      cost_total_max: isDefaultCost ? undefined : values.cost[1],
      company_id: hasDefaultCompanyIds ? undefined : values.companyIds,
    }).finally(() => {
      setFiltersAreApplying(false);
    });
  }

  const renderCenters = () => {
    if (filtersAreApplying) {
      return <Preloader />
    }

    return (<>
      {Boolean(totalCenters) && <Divider sx={{marginBottom: 3}}/>}
      {!totalCenters && <>
        <div className='search-results__results'>
          <Divider sx={{marginBottom: 3, width: '100%'}}/>
          <Typography variant='h6'>
            {t('actions__message__searchNotFound')}
          </Typography>
        </div>
      </>}
      {centers.map((center, index) =>
        <CompanyCard
          key={index}
          center={center}
          onClose={handleClose}
          code={code}
          countryCode={countryCode}
          techReglament={techReglament}
        />)}
      {Number(totalCenters) > resultsCount && <Pagination
        count={Math.ceil(Number(totalCenters) / resultsCount)}
        sx={{marginTop: 3}}
        size='large'
        onChange={handleChangeCurrentPage}
      />}
    </>)
  }

  return (
    <div className='modal-search-container'>
      <div className='search-results'>
        {showProgressBar ? <CircularProgress/> : (
          <div className='search-results__container'>
            <div className='search-results__results'>
              <div className='search-results__sorting'>
                <FormControl sx={{width: '324px'}}>
                  <InputLabel shrink id='sorting-select'>{t('common__text__sort')}</InputLabel>
                  <Select
                    notched
                    labelId='sorting-select'
                    id='sorting-select'
                    value={filterData.order_by || SORTING_OPTIONS.COST_DESK}
                    label={t('common__text__sort')}
                    onChange={({target: {value}}) => centersRequest({
                      order_by: value
                    })}
                    className='search-results__select-sorting'>
                    <MenuItem
                      value={SORTING_OPTIONS.COST_DESK}>{t('dictionaries__text__certification__priceAscendingOrder')}</MenuItem>
                    <MenuItem
                      value={SORTING_OPTIONS.COST_ASC}>{t('dictionaries__text__certification__priceDescendingOrder')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel shrink id='count-select'>{t('common__text__resultsCount')}</InputLabel>
                  <Select
                    notched
                    labelId='count-select'
                    id='count-select'
                    value={resultsCount}
                    label={t('common__text__resultsCount')}
                    onChange={({target: {value}}) => handleChangeResultsCount(value)}
                    className='search-results__select-count'>
                    <MenuItem value={RESULTS_COUNT.COUNT_10}>10</MenuItem>
                    <MenuItem value={RESULTS_COUNT.COUNT_30}>30</MenuItem>
                    <MenuItem value={RESULTS_COUNT.COUNT_45}>45</MenuItem>
                  </Select>
                </FormControl>
              </div>
              {renderCenters()}
            </div>
            <Card className='search-results__filters'>
              <CardContent sx={{
                padding: 0,
                '&:last-child': {
                  paddingBottom: 0
                }
              }}>
                <Typography variant='h6' gutterBottom sx={{padding: '16px 24px 10px'}}>
                  {t('common__text__filters')}
                </Typography>
                <Filters
                  applyButtonText={t('actions__text__applyFilters')}
                  onApply={(values) => handleApplyFilters(values as FilterValues)}
                  onChange={(values) => handleChangeFilters(values as FilterValues)}
                  sections={[ {
                    type: 'range',
                    key: 'cost',
                    title: t('dictionaries__text__certification__costOfServices'),
                    defaultValues: [ minTotal, maxTotal ],
                    min: minTotal,
                    max: maxTotal,
                    selectedValues: filtersValues.cost,
                    fromLabel: t('dictionaries__text__certification__costOfServicesFrom'),
                    toLabel: t('dictionaries__text__certification__costOfServicesTo'),
                    valuePrefix: 'USD',
                    inputAdornment: ADORNMENTS.PRICE,
                  }, {
                    key: 'companyIds',
                    title: t('dictionaries__text__additional__serviceProviders'),
                    defaultValues: companyIds,
                    selectedValues: filtersValues.companyIds,
                    options: companyOptions,
                  } ]}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default ModalSearch
